import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import CoachCard from './components/CoachCard';
import FilterSection from './components/FilterSection';
import BookingModal from '../../components/feature/BookingModal';
import { useSEO } from '../../utils/seo';

interface Coach {
  id: string;
  specialty: string;
  experience_years: number;
  hourly_rate: number;
  location: string;
  description: string;
  languages: string[];
  rating: number;
  total_reviews: number;
  users: {
    first_name: string;
    last_name: string;
    avatar_url: string | null;
  };
}

export default function Coachs() {
  useSEO({
    title: 'Nos Coachs Sportifs Certifiés - Trouvez votre Coach Personnel',
    description: 'Découvrez notre équipe de plus de 150 coachs sportifs certifiés partout en France. Spécialistes en musculation, yoga, nutrition, cardio et HIIT. Réservez votre coach à domicile dès maintenant.',
    keywords: 'coachs sportifs, coach personnel, entraîneur certifié, coach musculation, coach yoga, coach nutrition, coach à domicile',
    canonical: '/coachs',
    ogImage: 'https://public.readdy.ai/ai/img_res/b34f98cb8bf16c3905645f29c306c337.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Coachs Sportifs Certifiés CoachFit",
      "description": "Liste de nos coachs sportifs professionnels certifiés",
      "url": `${import.meta.env.VITE_SITE_URL || ''}/coachs`,
      "numberOfItems": "150+"
    }
  });

  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [filteredCoaches, setFilteredCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    specialty: '',
    location: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'rating'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Récupérer les paramètres URL au chargement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const locationParam = urlParams.get('location');
    
    if (locationParam) {
      setFilters(prev => ({
        ...prev,
        location: locationParam
      }));
    }
  }, []);

  useEffect(() => {
    fetchCoaches();
  }, [filters]);

  useEffect(() => {
    applyFilters();
  }, [coaches, searchTerm]);

  const fetchCoaches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.specialty) params.append('specialty', filters.specialty);
      if (filters.location) params.append('location', filters.location);
      if (filters.minPrice) params.append('minPrice', filters.minPrice);
      if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-coaches?${params}`);
      const result = await response.json();
      
      if (response.ok) {
        setCoaches(result.coaches || []);
      } else {
        console.error('Erreur lors du chargement des coachs:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des coachs:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = coaches;

    if (searchTerm) {
      filtered = filtered.filter(coach => 
        `${coach.users.first_name} ${coach.users.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coach.specialty.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCoaches(filtered);
  };

  const handleBooking = (coach: Coach) => {
    setSelectedCoach(coach);
    setIsBookingModalOpen(true);
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters({
      specialty: newFilters.specialty || '',
      location: newFilters.location || '',
      minPrice: newFilters.minPrice || '',
      maxPrice: newFilters.maxPrice || '',
      sortBy: newFilters.sortBy || 'rating'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-20 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.8), rgba(37, 99, 235, 0.8)), url('https://readdy.ai/api/search-image?query=Team%20of%20professional%20fitness%20coaches%20in%20modern%20gym%2C%20diverse%20group%20of%20trainers%2C%20motivational%20atmosphere%2C%20coaching%20expertise%2C%20professional%20fitness%20environment&width=1200&height=600&seq=coaches-hero&orientation=landscape')`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nos Coachs Experts
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Découvrez notre équipe de coachs professionnels certifiés, prêts à vous accompagner vers vos objectifs
            </p>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="py-8 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md mx-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Rechercher un coach ou une spécialité..."
                />
              </div>
            </div>

            <FilterSection 
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Results Count */}
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                {loading ? 'Chargement...' : `${filteredCoaches.length} coach${filteredCoaches.length > 1 ? 's' : ''} trouvé${filteredCoaches.length > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
        </section>

        {/* Coaches Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Chargement des coachs...</p>
              </div>
            ) : filteredCoaches.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-user-search-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun coach trouvé</h3>
                <p className="text-gray-600">Essayez de modifier vos critères de recherche</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredCoaches.map((coach) => (
                  <CoachCard
                    key={coach.id}
                    coach={{
                      id: coach.id,
                      name: `${coach.users.first_name} ${coach.users.last_name}`,
                      specialty: coach.specialty,
                      experience: coach.experience_years,
                      price: coach.hourly_rate,
                      location: coach.location,
                      rating: coach.rating,
                      reviews: coach.total_reviews,
                      languages: coach.languages,
                      description: coach.description,
                      avatar: coach.users.avatar_url || undefined
                    }}
                    onBook={() => handleBooking(coach)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Pourquoi choisir nos coachs ?
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-medal-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">150+</h3>
                <p className="text-gray-600">Coachs certifiés</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">4.8/5</h3>
                <p className="text-gray-600">Note moyenne</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">24h</h3>
                <p className="text-gray-600">Réponse garantie</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">100%</h3>
                <p className="text-gray-600">Satisfaction</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />

      {/* Booking Modal */}
      {selectedCoach && (
        <BookingModal
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedCoach(null);
          }}
          coach={{
            id: selectedCoach.id,
            name: `${selectedCoach.users.first_name} ${selectedCoach.users.last_name}`,
            specialty: selectedCoach.specialty,
            hourlyRate: selectedCoach.hourly_rate,
            avatar: selectedCoach.users.avatar_url || undefined
          }}
        />
      )}
    </div>
  );
}
